/**
 * @engine/input — Sensor Devices
 *
 * Accelerometer, gyroscope, and other motion sensors.
 */

import type { SensorControl } from '../types';
import { InputDevice } from './InputDevice';
import { Vector3Control } from '../controls/Vector3Control';

/**
 * Accelerometer device — measures acceleration in m/s².
 */
export class Accelerometer extends InputDevice {
  /** Raw acceleration including gravity */
  readonly acceleration: Vector3Control;

  /** Gravity vector */
  readonly gravity: Vector3Control;

  /** Linear acceleration (without gravity) */
  readonly linearAcceleration: Vector3Control;

  constructor(index: number = 0) {
    super('accelerometer', index, 'Accelerometer');

    this.acceleration = new Vector3Control('acceleration');
    this.gravity = new Vector3Control('gravity');
    this.linearAcceleration = new Vector3Control('linearAcceleration');

    this.registerControl(this.acceleration);
    this.registerControl(this.gravity);
    this.registerControl(this.linearAcceleration);
  }

  /**
   * Update from DeviceMotionEvent
   */
  updateFromDeviceMotion(
    event: {
      accelerationIncludingGravity?: { x: number | null; y: number | null; z: number | null } | null;
      acceleration?: { x: number | null; y: number | null; z: number | null } | null;
    },
    time: number
  ): void {
    const aig = event.accelerationIncludingGravity;
    const a = event.acceleration;

    if (aig) {
      this.acceleration.update(
        {
          x: aig.x ?? 0,
          y: aig.y ?? 0,
          z: aig.z ?? 0,
        },
        time
      );
    }

    if (a) {
      this.linearAcceleration.update(
        {
          x: a.x ?? 0,
          y: a.y ?? 0,
          z: a.z ?? 0,
        },
        time
      );

      // Compute gravity as difference
      if (aig) {
        this.gravity.update(
          {
            x: (aig.x ?? 0) - (a.x ?? 0),
            y: (aig.y ?? 0) - (a.y ?? 0),
            z: (aig.z ?? 0) - (a.z ?? 0),
          },
          time
        );
      }
    }
  }
}

/**
 * Gyroscope device — measures angular velocity in rad/s.
 */
export class Gyroscope extends InputDevice {
  /** Angular velocity (rad/s) */
  readonly angularVelocity: Vector3Control;

  constructor(index: number = 0) {
    super('gyroscope', index, 'Gyroscope');

    this.angularVelocity = new Vector3Control('angularVelocity');
    this.registerControl(this.angularVelocity);
  }

  /**
   * Update from DeviceMotionEvent
   */
  updateFromDeviceMotion(
    event: {
      rotationRate?: { alpha: number | null; beta: number | null; gamma: number | null } | null;
    },
    time: number
  ): void {
    const rate = event.rotationRate;
    if (!rate) return;

    // Convert from degrees/s to rad/s
    const DEG_TO_RAD = Math.PI / 180;
    this.angularVelocity.update(
      {
        x: (rate.beta ?? 0) * DEG_TO_RAD,
        y: (rate.gamma ?? 0) * DEG_TO_RAD,
        z: (rate.alpha ?? 0) * DEG_TO_RAD,
      },
      time
    );
  }
}
